import React, { useEffect, useRef } from 'react'
// The linter does not like that this is capitalized. How dare we capitilize this export.
import { Buttons as buttons } from '@paypal/checkout-components/dist/button.js'
import { html } from '@krakenjs/jsx-pragmatic'

/**
 * Override some of the buttons CSS since the buttons are normally
 * inside an iframe.  The override CSS mirrors what we are doing in
 * checkout-components without the need for media queries.
 */
const buttonsStyleOverrides = `
.docs-button-loader {
  position: relative;
  width: 100%;
  min-width: 200px;
}

@media screen and (min-width: 1024px) {
  .docs-button-loader {
    min-width: 250px;
  }
}

@media screen and (min-width: 1200px) {
  .docs-button-loader {
    min-width: 300px;
  }
}
.docs-button-loader .paypal-button-container {
      min-width: unset;
      width: 100%;
    }
    .docs-button-loader .paypal-button .paypal-button-label-container {
      margin: 0 4%;
s}`

interface ButtonLoaderProps {
  style?: {
    color?: 'gold' | 'blue' | 'silver' | 'white' | 'black'
    disableMaxWidth?: boolean
    height?: number
    label?:
      | 'paypal'
      | 'checkout'
      | 'buynow'
      | 'pay'
      | 'installment'
      | 'subscribe'
      | 'donate'
    layout?: 'vertical' | 'horizontal'
    shape?: 'rect' | 'pill' | 'sharp'
    tagline?: boolean
  }
  fundingSource: string
}

/**
 * @name ButtonLoader
 * @description Loads the smart button UI from checkout-components.
 * Note that the buttons loaded by this component are merely for
 * displaying the UI and will not have any functionality on click.
 */
export const ButtonLoader: React.FC<ButtonLoaderProps> = ({
  style,
  fundingSource = 'paypal',
}) => {
  const container = useRef<HTMLDivElement>(null)
  const buttonsProps = {
    style: {
      height: 40,
      ...style,
    },

    /**
     * fundingSource is defaulted to 'paypal' since most of the
     * time in the docs,we are only showing the paypal button
     */
    fundingSource,

    /**
     * Dummy onClick prop is required.
     */
    onClick: () => {},
    fundingEligibility: getGenericFundingEligibility(),
    content: getGenericContent(),
    clientID: 'test',
    env: 'sandbox',
    locale: {
      country: 'US',
      lang: 'en',
    },
    tagline: true,
  }

  // Render the buttons as HTML
  const buttonsHTML = buttons(buttonsProps).render(html())

  /**
   * Using vanilla JS syntax here is not ideal, but it is reliable.
   * It allows us to create a shadow root, which will scope all of the buttons CSS styles inside this component.
   * Without the shadow root, the buttons HTML will set many unwanted styles on the rest of the page.
   */
  useEffect(() => {
    // Create stylesheet to override styles.
    // Had to do this dynamically so that it will come before buttonsHTMLContainer
    const stylesheet = document.createElement('style')
    stylesheet.innerHTML = buttonsStyleOverrides

    // Create the container for the buttons with appropriate classes
    const buttonsHTMLContainer = document.createElement('div')
    buttonsHTMLContainer.innerHTML = buttonsHTML
    buttonsHTMLContainer.classList.add('dom-ready')
    buttonsHTMLContainer.classList.add('docs-button-loader')

    // Attach shadow to div.  This is what scopes the CSS so no styles from the buttons HTML will leak into the rest of the page
    const shadow = container!.current!.attachShadow({ mode: 'open' })

    shadow.appendChild(stylesheet)
    shadow.appendChild(buttonsHTMLContainer)
  }, [])

  return <div ref={container} />
}

// Content pulled from https://github.com/paypal/paypal-checkout-components/blob/main/test/content.js
function getGenericContent() {
  return {
    instantlyPayWith: 'Pay instantly with',
    poweredBy: 'Powered by PayPal',
    chooseCardOrShipping: 'Choose card or shipping',
    useDifferentAccount: 'Use different account',
    deleteVaultedAccount: 'Forget this account',
    deleteVaultedCard: 'Forget this card',
    chooseCard: 'Choose card',
    payWithDebitOrCreditCard: 'Debit or Credit Card',
    payWithDifferentAccount: 'Pay with a different account',
    payWithDifferentMethod: 'Pay with a different funding method',
    payNow: 'Pay Now',
    payWith: 'Pay with',
    credit: 'Credit',
    balance: 'Balance',
    payPalBalance: 'PayPal balance',
    moreOptions: 'More options',
    label: {
      checkout: 'Checkout',
      paypal: 'PayPal',
      buynow: 'Buy Now',
      pay: 'Pay with PayPal',
      installment: {
        withPeriod: 'Pay up to {period}x without interest',
        withoutPeriod: 'Interest free payments',
      },
    },
  }
}

function getGenericFundingEligibility() {
  return {
    paypal: {
      eligible: true,
      vaultable: true,
    },
    paylater: {
      eligible: true,
      vaultable: false,
      products: {
        payIn3: {
          eligible: false,
          variant: null,
        },
        payIn4: {
          eligible: false,
          variant: null,
        },
        paylater: {
          eligible: true,
          variant: null,
        },
      },
    },
    card: {
      eligible: true,
      branded: true,
      installments: false,
      vendors: {
        visa: {
          eligible: true,
          vaultable: true,
        },
        mastercard: {
          eligible: true,
          vaultable: true,
        },
        amex: {
          eligible: true,
          vaultable: true,
        },
        discover: {
          eligible: true,
          vaultable: true,
        },
        hiper: {
          eligible: false,
          vaultable: false,
        },
        elo: {
          eligible: false,
          vaultable: true,
        },
        jcb: {
          eligible: false,
          vaultable: true,
        },
      },
      guestEnabled: false,
    },
    venmo: {
      eligible: true,
      vaultable: true,
    },
    itau: {
      eligible: false,
    },
    credit: {
      eligible: false,
    },
    applepay: {
      eligible: false,
    },
    sepa: {
      eligible: false,
    },
    ideal: {
      eligible: false,
    },
    bancontact: {
      eligible: false,
    },
    giropay: {
      eligible: false,
    },
    eps: {
      eligible: false,
    },
    sofort: {
      eligible: false,
    },
    mybank: {
      eligible: false,
    },
    p24: {
      eligible: false,
    },
    wechatpay: {
      eligible: false,
    },
    payu: {
      eligible: false,
    },
    blik: {
      eligible: false,
    },
    trustly: {
      eligible: false,
    },
    oxxo: {
      eligible: false,
    },
    boleto: {
      eligible: false,
    },
    boletobancario: {
      eligible: false,
    },
    mercadopago: {
      eligible: false,
    },
    multibanco: {
      eligible: false,
    },
    satispay: {
      eligible: false,
    },
    paidy: {
      eligible: false,
    },
  }
}
